import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Skeleton } from '@gitbook/spine-react';

import { UserAvatarSkeleton } from '../Users';
import { CommentMessage } from './CommentMessage';
import { useListPaginatedCommentReplies } from './useListPaginatedCommentReplies';

export type CommentThreadProps = {
    /* The comment to display. */
    comment: api.Comment;
};

/**
 * Displays a comment message along with replies, when toggled to show.
 */
export function CommentThread(props: CommentThreadProps): React.ReactElement | null {
    const { comment } = props;

    const [commentReplies] = useListPaginatedCommentReplies({
        spaceId: comment.target.space,
        commentId: comment.id,
        changeRequestId: comment.target.changeRequest,
    });

    if (commentReplies.length) {
        return (
            <div className="flex flex-col gap-2 pt-1">
                {commentReplies.map((reply, index) => (
                    <CommentMessage
                        key={reply.id}
                        comment={comment}
                        commentReply={reply}
                        isLastReply={index === commentReplies.length - 1}
                    />
                ))}
            </div>
        );
    }

    return null;
}

export const CommentThreadSkeleton = (props: { count: number }) => {
    const { count } = props;

    return (
        <div className="flex flex-col gap-2">
            {Array.from({ length: count }, (_, i) => (
                <div key={i} className="flex gap-4 py-2">
                    <UserAvatarSkeleton size="sm" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-1/3" />
                        <Skeleton className="h-4 w-2/3" />
                    </div>
                </div>
            ))}
        </div>
    );
};
